/**
 * InspectionsListing — modern Inspections listing using FilterBar + Quimby Table.
 *
 * Replaces the legacy Kendo Grid / FilterPanel / SearchPanel / ColumnMenu shell.
 *
 * Preserved behaviors:
 *   - All current column cells (id link, status inline-edit, description wbr,
 *     scheduledDate series link, IIM unresolved-issues badge).
 *   - FilterBar with all 27 current filters + context-hidden and optional gate rules.
 *   - Toolbar mass actions: Mass Update, Generate Reports, Export CSV,
 *     push-to-TCE / IROL / LivSafe (optional gates), building context restrictions.
 *   - Selection state and Toolbar (including Toolbar.Selection and ColumnVisibilityControl)
 *     are now owned by the Table molecule (table-owned-toolbar-selection plan, phase-3/task-03).
 *   - select-current-page (header checkbox) and select-all-entirely with
 *     IP-14655 narrowing parity + count mismatch guard in MassUpdateModal.
 *   - Page sizes 25 / 50 / 100, sticky primary (id) column.
 *   - Loading / empty / retryable error states without stale selectable rows.
 *   - Live usePublishListContext publication.
 *   - move-series DOM disabled semantics.
 *   - Column visibility with immediate checkbox updates and Reset.
 *   - Legacy localStorage preference migration via useInspectionsPreferences.
 *
 * Standalone context (showHeader=true):
 *   - Renders WorkspaceHeader with title, optional subtitle, and optional New Inspection action button.
 *   - Column visibility (Configure) lives on the Table-owned toolbar (pinned right, trailing group).
 *   - Authorization metadata (pageTitle, headerSubtitle, newInspectionUrl, canCreateInspection,
 *     newInspectionLabel, newInspectionForbiddenMessage) passed from Rails ERB.
 *
 * Embedded contexts (building tab, account tab):
 *   - Flag-ON mounts pass showHeader=true so the listing owns WorkspaceHeader (including
 *     the building-tab iPad note as subtitle). Flag-OFF keeps legacy ERB chrome.
 *
 * Hook adoption:
 *   - A1  useListingScopeFromProps
 *   - A2  useListingPreferenceBootstrap
 *   - A3  usePersistedListingPreferences
 *   - A4  createListingLookupLoaders — skipped; paged-cursor loader type is
 *         incompatible with factory's LoadOptionsFn; service fetchers kept as-is
 *   - A5  useListingFilterBarControllers (clearSelection omitted; resets via selection.resetKey)
 *   - A6  useDebouncedListingSearch (onNarrow omitted; resets via selection.resetKey)
 *   - A7  useListingTableControllers (clearSelection omitted; resets via selection.resetKey)
 *   - A8/A9 removed — selection now lives in the Table molecule via selection prop
 *   - A10 useListingColumnVisibilityState
 *   - A11 useListingQueryViewModel
 *   - A12 useOptimisticListingRowPatch (wrapped for enableMoveSeries side-effect)
 *   - A13 useListingInvalidationOnMutation (clearSelection omitted; mutation bump in resetKey)
 *   - A14 createNarrowingParams (toMassActionsListingState removed)
 *   - A15 usePublishListContextBridge
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useQueryClient } from '@tanstack/react-query';
import { Table, TableSize } from '@ui/molecules/Table/Table';
import { FilterBar } from '@ui/organisms/FilterBar';
import { WorkspaceHeader } from '@ui/molecules/Header/WorkspaceHeader/WorkspaceHeader';
import I18n, { translate } from '@utils/i18n';
import { loadSelectedFromOptions } from '@helpers/lookupByIds';
import InspectionAssistantModal from '@features/InspectionAssistantModal';
import { TimeZoneContextProvider } from '@contexts/timezone-context';
import { KendoIntlProvider } from '@contexts/kendo-intl-context';
import { useListingQueryState, useListingScopeFromProps, useListingPreferenceBootstrap, usePersistedListingPreferences, useListingFilterBarControllers, useDebouncedListingSearch, useListingTableControllers, useListingColumnVisibilityState, useListingQueryViewModel, useOptimisticListingRowPatch, useListingInvalidationOnMutation, createNarrowingParams, usePublishListContextBridge } from '@hooks/listings';
import { INITIAL_INSPECTION_FILTERS, DEFAULT_ACTIVE_FILTER_KEYS } from './types';
import { useInspectionsPreferences } from './hooks/useInspectionsPreferences';
import { useInspectionsFilterConfig } from './hooks/useInspectionsFilterConfig';
import { useInspectionsQuery } from './hooks/useInspectionsQuery';
import { inspectionsKeys } from './hooks/queryKeys';
import { columnPolicyForScope } from './adapters/inspectionsColumnPolicy';
import { createAccountsLoader, createAdditionalTerritoriesLoader, createBuildingsLoader, createTagsLoader, createBuildingsByIdsLoader, createAccountsByIdsLoader, createTagsByIdsLoader, createAdditionalTerritoriesByIdsLoader } from './services/InspectionsLookupService';
import { createArrayLoader } from './adapters/createArrayLoader';
import { serializeInspectionsNarrowing } from './adapters/inspectionsStateSerializer';
import { InspectionsListingProvider } from './components/InspectionsListingContext';
import { buildVisibleColumns, buildColumnVisibilityItems } from './components/InspectionsColumns';
import MassActions from './nested/MassActions';
import './index.css';
// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------
const NS = 'features.inspections_index';
const PAGE_SIZE_OPTIONS = [25, 50, 100];
const DEFAULT_SORT = { field: 'id', direction: 'desc' };
// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
export function InspectionsListing({ buildingId = null, inspectionSeriesId = null, accountId = null, frequenciesForSelect = [], inspectionTypesForSelect = [], statusesForSelect = [], techniciansForSelect = [], inactiveTechniciansForSelect = [], technicianTeamsForSelect = [], subcontractorsForSelect, territoriesForSelect = [], assetsForSelect = [], optionalFilters = {}, optionalMassActions = {}, iimEnabled = false, enableMoveSeries = null, timeZone = null, showHeader = false, pageTitle = '', headerSubtitle = '', newInspectionUrl = null, canCreateInspection = false, newInspectionLabel = '', newInspectionForbiddenMessage = '' }) {
    var _a, _b, _c, _d, _e, _f, _g, _h;
    const queryClient = useQueryClient();
    // --- A1: Scope ---
    const scope = useListingScopeFromProps({ buildingId, inspectionSeriesId, accountId });
    // --- Preferences (page-specific; wraps useListingPreferences with Inspections cache key) ---
    const { preferences, savePreferences, clearPreferences } = useInspectionsPreferences({
        scope: scope,
        iimEnabled
    });
    // --- Column policy (page-specific) ---
    const policy = useMemo(() => columnPolicyForScope(scope, iimEnabled), [scope, iimEnabled]);
    // --- A2: Bootstrap initial state values from preferences ---
    const { initialActiveFilterKeys, initialVisibleColumnKeys, initialPageSize, initialSort, initialFilters } = useListingPreferenceBootstrap({
        preferences,
        defaultActiveFilterKeys: [...DEFAULT_ACTIVE_FILTER_KEYS],
        columnPolicy: policy,
        defaultFilters: INITIAL_INSPECTION_FILTERS,
        defaultSort: DEFAULT_SORT,
        defaultPageSize: 25,
        includeSearch: true
    });
    // --- Query state ---
    const { state, setFilters, setSearch, setSort, setPageNumber, setPageSize } = useListingQueryState({
        scope: scope,
        page: { number: 1, size: initialPageSize },
        sort: initialSort,
        filters: initialFilters,
        search: ''
    });
    // --- Loaders (A4 skipped: paged-cursor loader type incompatible with factory's LoadOptionsFn) ---
    const loadBuildings = useMemo(() => createBuildingsLoader(), []);
    const loadAccounts = useMemo(() => createAccountsLoader(), []);
    const loadTags = useMemo(() => createTagsLoader(), []);
    const loadAdditionalTerritories = useMemo(() => createAdditionalTerritoriesLoader(), []);
    const loadTerritories = useMemo(() => createArrayLoader(territoriesForSelect), [territoriesForSelect]);
    const loadSelectedBuildings = useMemo(() => createBuildingsByIdsLoader(), []);
    const loadSelectedAccounts = useMemo(() => createAccountsByIdsLoader(), []);
    const loadSelectedTags = useMemo(() => createTagsByIdsLoader(), []);
    const loadSelectedAdditionalTerritories = useMemo(() => createAdditionalTerritoriesByIdsLoader(), []);
    const loadSelectedTerritories = useMemo(() => loadSelectedFromOptions(territoriesForSelect), [territoriesForSelect]);
    // --- Query + A11: View model ---
    const inspectionsQuery = useInspectionsQuery(state);
    const { rows, totalCount, totalPages, isLoading, isError, refetch } = useListingQueryViewModel({
        query: inspectionsQuery,
        pageSize: state.page.size,
        getTotalCount: (d) => d.total_count
    });
    // --- move-series enablement (domain-specific DOM flag; updated from API responses) ---
    const [moveSeriesEnabled, setMoveSeriesEnabled] = useState(enableMoveSeries !== null && enableMoveSeries !== void 0 ? enableMoveSeries : null);
    // --- Include inactive technician state (local; not persisted in preferences) ---
    const [includeInactive, setIncludeInactive] = useState(false);
    // --- Mutation counter — bumped by onDataChanged to fold into resetKey ---
    const [mutationCount, setMutationCount] = useState(0);
    // --- A5: Filter bar controllers (resets via selection.resetKey) ---
    const { activeFilterKeys, handleFiltersChange, handleFilterToggle, handleResetFilters, handleResetCache } = useListingFilterBarControllers({
        setFilters,
        initialFilters: INITIAL_INSPECTION_FILTERS,
        initialActiveFilterKeys,
        clearPreferences
    });
    // --- A6: Debounced search (onNarrow omitted; resets via selection.resetKey) ---
    const { searchInput, handleSearchChange } = useDebouncedListingSearch({
        setSearch
    });
    // --- A7: Table navigation controllers (resets via selection.resetKey) ---
    const { handleSort, handlePageChange, handlePageSizeChange } = useListingTableControllers({
        setSort,
        setPageNumber,
        setPageSize
    });
    // --- A10: Column visibility state ---
    // Wrap column builders to match hook's generic TableColumn<Record<string,unknown>> signature.
    const buildVisibleColumnsCasted = useCallback((keys) => buildVisibleColumns(keys), []);
    const buildVisibilityItemsFn = useCallback((p) => buildColumnVisibilityItems(p.allowed, p.required, iimEnabled), [iimEnabled]);
    const { visibleColumnKeys, visibleColumns: visibleColumnsRaw, columnVisibilityItems, sortableColumns, handleColumnVisibilityApply } = useListingColumnVisibilityState({
        policy,
        initialVisibleColumnKeys,
        buildVisibleColumns: buildVisibleColumnsCasted,
        buildVisibilityItems: buildVisibilityItemsFn,
        nonSortableKeys: ['unresolvedIssues', 'visits']
    });
    // Re-type for Table<InspectionRow> which needs the domain-specific column type.
    const visibleColumns = visibleColumnsRaw;
    // --- A3: Persist preferences ---
    usePersistedListingPreferences({
        savePreferences,
        pageSize: state.page.size,
        sort: state.sort,
        filters: state.filters,
        activeFilterKeys,
        visibleColumnKeys
    });
    // --- A12: Optimistic row patch (cache update + domain-specific enableMoveSeries side-effect) ---
    const patchRow = useOptimisticListingRowPatch({
        queryClient,
        queryKey: [...inspectionsKeys.listing(state)]
    });
    const onRowUpdate = useCallback((updatedRow) => {
        patchRow(updatedRow);
        const row = updatedRow;
        if (typeof row.enableMoveSeries === 'boolean') {
            setMoveSeriesEnabled(row.enableMoveSeries);
        }
    }, [patchRow]);
    // --- A13: Invalidation on mutation (post-mutation clear via mutationCount in resetKey) ---
    const { successMessage, setSuccessMessage, onDataChanged: baseOnDataChanged } = useListingInvalidationOnMutation({
        queryClient,
        queryKey: [...inspectionsKeys.all]
    });
    const onDataChanged = useCallback((message, updatedData) => {
        baseOnDataChanged(message);
        setMutationCount((c) => c + 1);
        if (typeof (updatedData === null || updatedData === void 0 ? void 0 : updatedData.enableMoveSeries) === 'boolean') {
            setMoveSeriesEnabled(updatedData.enableMoveSeries);
        }
    }, [baseOnDataChanged]);
    // --- A14: Narrowing params ---
    // serializeInspectionsNarrowing strips page and sort (IP-14655: only filters+search+scope).
    // resetKey composes narrowing + page + size + sort + mutationCount so all navigation
    // and post-mutation events clear Table-owned selection.
    const narrowingQueryString = useMemo(() => serializeInspectionsNarrowing(state).toString(), [state]);
    const narrowingParams = createNarrowingParams(narrowingQueryString);
    const selectionResetKey = `${narrowingQueryString}|${state.page.number}|${state.page.size}|${(_b = (_a = state.sort) === null || _a === void 0 ? void 0 : _a.field) !== null && _b !== void 0 ? _b : ''}|${(_d = (_c = state.sort) === null || _c === void 0 ? void 0 : _c.direction) !== null && _d !== void 0 ? _d : ''}|${mutationCount}`;
    // --- A15: Publish list context bridge ---
    usePublishListContextBridge({
        page: state.page,
        sort: state.sort,
        filters: state.filters,
        search: state.search,
        totalCount,
        rows
    });
    // --- move-series DOM effect (domain-specific; not generalized) ---
    useEffect(() => {
        const btn = document.getElementById('move-series');
        const wrapper = document.getElementById('move-series-wrapper');
        if (btn)
            btn.disabled = !moveSeriesEnabled;
        if (wrapper) {
            wrapper.title = moveSeriesEnabled
                ? ''
                : I18n.t('inspection_series.show.inspection_series_push_series_modal.disabled_tooltip');
        }
    }, [moveSeriesEnabled]);
    // --- Filter config (page-specific) ---
    const { filters: filterConfigs } = useInspectionsFilterConfig({
        scope: scope,
        filters: state.filters,
        activeFilterKeys,
        onChange: handleFiltersChange,
        onToggle: handleFilterToggle,
        frequenciesForSelect,
        statusesForSelect,
        technicianTeamsForSelect,
        techniciansForSelect,
        inactiveTechniciansForSelect,
        assetsForSelect,
        subcontractorsForSelect,
        inspectionTypesForSelect,
        includeInactive,
        onIncludeInactiveChange: setIncludeInactive,
        includeInactiveLabel: I18n.t('filters.include_inactive'),
        loadBuildings,
        loadAccounts,
        loadTerritories,
        loadTags,
        loadAdditionalTerritories,
        loadSelectedBuildings,
        loadSelectedAccounts,
        loadSelectedTerritories,
        loadSelectedTags,
        loadSelectedAdditionalTerritories,
        optionalFilters
    });
    // ---------------------------------------------------------------------------
    // Render
    // ---------------------------------------------------------------------------
    const tableErrorContent = (React.createElement("tr", null,
        React.createElement("td", { colSpan: visibleColumns.length + 1, className: "inspections-listing__error" },
            React.createElement("i", { className: "fa-light fa-triangle-exclamation" }),
            React.createElement("span", null, I18n.t('generic.error')),
            React.createElement("button", { type: "button", className: "qmb-control--sm", onClick: () => {
                    void refetch();
                } }, I18n.t('generic.retry')))));
    const tableEmptyContent = (React.createElement("tr", null,
        React.createElement("td", { colSpan: visibleColumns.length + 1, className: "inspections-listing__empty" },
            React.createElement("span", null, translate('empty_state', { namespace: NS, defaultValue: 'No inspections found.' })))));
    const newInspectionAction = useMemo(() => {
        if (!showHeader || !newInspectionLabel || !newInspectionUrl)
            return undefined;
        if (canCreateInspection) {
            return (React.createElement("a", { href: newInspectionUrl, className: "qmb-ui-button qmb-ui-button--primary" },
                React.createElement("i", { className: "fa-light fa-circle-plus" }),
                ` ${newInspectionLabel}`));
        }
        return (React.createElement("span", { "data-toggle": "popover", "data-html": "true", "data-content": newInspectionForbiddenMessage, "data-placement": "bottom" },
            React.createElement("button", { type: "button", className: "qmb-ui-button qmb-ui-button--primary", disabled: true },
                React.createElement("i", { className: "fa-light fa-circle-plus" }),
                ` ${newInspectionLabel}`)));
    }, [showHeader, newInspectionLabel, canCreateInspection, newInspectionUrl, newInspectionForbiddenMessage]);
    return (React.createElement(TimeZoneContextProvider, { initialTimezone: timeZone !== null && timeZone !== void 0 ? timeZone : undefined },
        React.createElement(KendoIntlProvider, null,
            React.createElement(InspectionsListingProvider, { value: { statusesForSelect, onRowUpdate } },
                showHeader && pageTitle && (React.createElement(WorkspaceHeader, { divider: false, title: pageTitle, subtitle: headerSubtitle || undefined, actions: newInspectionAction })),
                successMessage && (React.createElement("section", { className: "workspace__section--alerts" },
                    React.createElement("div", { className: "qmb-alert --success" },
                        React.createElement("i", { className: "alert__visual" }),
                        React.createElement("span", { id: "success_alert_text" }, successMessage),
                        React.createElement("button", { type: "button", "aria-label": I18n.t('generic.close'), className: "qmb-control--icon--close fa-light fa-xmark", onClick: () => setSuccessMessage('') })))),
                React.createElement(Table, { data: rows, columns: visibleColumns, variant: "listing", size: TableSize.Full, selectable: true, getItemId: (row) => row.id, sortable: true, sortableColumns: sortableColumns, onSort: handleSort, sortBy: (_f = (_e = state.sort) === null || _e === void 0 ? void 0 : _e.field) !== null && _f !== void 0 ? _f : null, sortDirection: ((_h = (_g = state.sort) === null || _g === void 0 ? void 0 : _g.direction) !== null && _h !== void 0 ? _h : null), onPageChange: handlePageChange, serverCurrentPage: state.page.number, serverTotalPages: totalPages, serverTotalItems: totalCount, pageSize: state.page.size, pageSizeOptions: PAGE_SIZE_OPTIONS, onPageSizeChange: handlePageSizeChange, isLoading: isLoading, isError: isError, errorContent: tableErrorContent, emptyContent: tableEmptyContent, enableCustomScrollbar: true, selection: {
                        mode: 'entire',
                        selectAllLabel: translate('selected_global_counter', {
                            namespace: 'features.inspections_index.mass_actions',
                            count: totalCount
                        }),
                        resetKey: selectionResetKey
                    }, toolbar: {
                        groups: [
                            {
                                id: 'inspections-mass-actions',
                                content: (selection) => (React.createElement(MassActions, { selection: selection, statusesForSelect: statusesForSelect, techniciansForSelect: techniciansForSelect, inspectionTypesForSelect: inspectionTypesForSelect, queryParams: narrowingParams, buildingId: buildingId !== null && buildingId !== void 0 ? buildingId : undefined, optionalMassActions: optionalMassActions, onDataChanged: onDataChanged, inspectionSeriesId: inspectionSeriesId !== null && inspectionSeriesId !== void 0 ? inspectionSeriesId : undefined }))
                            }
                        ]
                    }, filterBar: React.createElement(FilterBar, { filters: filterConfigs, onResetFilters: handleResetFilters, onFilterToggle: handleFilterToggle, showSearch: true, searchValue: searchInput, onSearchChange: handleSearchChange, searchPlaceholder: I18n.t('filters.search'), allowResetCache: process.env.RAILS_ENV !== 'production', onResetCache: handleResetCache }), columnVisibility: {
                        items: columnVisibilityItems,
                        visibleKeys: visibleColumnKeys,
                        defaultKeys: policy.defaults,
                        onApply: handleColumnVisibilityApply
                    } }),
                React.createElement(InspectionAssistantModal, null)))));
}
InspectionsListing.propTypes = {
    buildingId: PropTypes.number,
    inspectionSeriesId: PropTypes.number,
    accountId: PropTypes.number,
    frequenciesForSelect: PropTypes.array,
    inspectionTypesForSelect: PropTypes.array,
    statusesForSelect: PropTypes.array,
    techniciansForSelect: PropTypes.array,
    inactiveTechniciansForSelect: PropTypes.array,
    technicianTeamsForSelect: PropTypes.array,
    subcontractorsForSelect: PropTypes.array,
    territoriesForSelect: PropTypes.array,
    assetsForSelect: PropTypes.array,
    optionalFilters: PropTypes.object,
    optionalMassActions: PropTypes.object,
    iimEnabled: PropTypes.bool,
    enableMoveSeries: PropTypes.bool,
    timeZone: PropTypes.string,
    showHeader: PropTypes.bool,
    pageTitle: PropTypes.string,
    headerSubtitle: PropTypes.string,
    newInspectionUrl: PropTypes.string,
    canCreateInspection: PropTypes.bool,
    newInspectionLabel: PropTypes.string,
    newInspectionForbiddenMessage: PropTypes.string
};
